// How loud a voice is, for the orb — 16 Sep 2026 (orb.ts). Two readings a frame in voice mode: the
// recruiter's microphone and kadmon's voice as it plays, each an analyser on its stream.
//
// THE SCALE. Loudness in decibels below full scale, mapped so a quiet room reads near 0 and a voice
// near the microphone reads high: speech sits in the upper half, which is where the orb's onsets
// look for it. The mapping only has to separate talking from not talking and syllable from gap;
// it is never shown as a number.
//
// WHERE THERE IS NO READING — a browser without Web Audio, a stream it will not analyse, the phone
// check's scripted voice — `speechLike` makes a plausible one: syllables at a speaking rate with
// pauses between words, so the orb still says who is talking without claiming to hear anything.

/** The level of silence and of a raised voice, in dBFS. */
export const LEVEL_FLOOR_DB = -56;
export const LEVEL_CEILING_DB = -14;

/** One frame of samples (-1 to 1) as a level from 0 to 1. */
export function levelOfSamples(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i] ?? 0;
    sum += s * s;
  }
  const rms = Math.sqrt(sum / samples.length);
  if (!(rms > 0)) return 0;
  const db = 20 * Math.log10(rms);
  return Math.min(1, Math.max(0, (db - LEVEL_FLOOR_DB) / (LEVEL_CEILING_DB - LEVEL_FLOOR_DB)));
}

/** A level to read each frame, and the switch that lets its stream go. */
export interface LevelMeter {
  level(): number;
  close(): void;
}

/** What `streamMeter` needs of Web Audio — the browser's `AudioContext`, or a test's fake. */
export interface MeterAudio {
  createMediaStreamSource(stream: MediaStream): AudioNode;
  createAnalyser(): AnalyserNode;
  createGain(): GainNode;
  readonly destination: AudioNode;
}

/**
 * An analyser on a stream, heard by nobody: it runs into a gain of zero, because a Web Audio graph
 * that reaches no destination is not pulled in every browser, and the mic must not be played back
 * nor her voice played twice (it plays through its own element). The frame buffer is reused.
 */
export function streamMeter(audio: MeterAudio, stream: MediaStream): LevelMeter {
  const source = audio.createMediaStreamSource(stream);
  const analyser = audio.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0;
  const silent = audio.createGain();
  silent.gain.value = 0;
  source.connect(analyser);
  analyser.connect(silent);
  silent.connect(audio.destination);
  const frame = new Float32Array(analyser.fftSize);
  let open = true;
  return {
    level() {
      if (!open) return 0;
      analyser.getFloatTimeDomainData(frame);
      return levelOfSamples(frame);
    },
    close() {
      if (!open) return;
      open = false;
      source.disconnect();
      silent.disconnect();
    },
  };
}

/**
 * A made-up voice level: syllables four or five a second, each a quick rise and a slower fall,
 * with a pause every few syllables. Call with the time in milliseconds, increasing.
 */
export function speechLike(random: () => number = Math.random): (nowMs: number) => number {
  let next = 0;
  let peak = 0;
  let started = 0;
  let inWord = 0;
  return (nowMs) => {
    if (nowMs >= next) {
      if (inWord <= 0) {
        inWord = 2 + Math.floor(random() * 5);
        next = nowMs + 120 + random() * 200;
        peak = 0;
        return 0;
      }
      inWord -= 1;
      started = nowMs;
      peak = 0.45 + random() * 0.5;
      next = nowMs + 170 + random() * 90;
    }
    const t = nowMs - started;
    if (peak === 0) return 0;
    const envelope = t < 30 ? t / 30 : Math.exp(-(t - 30) / 110);
    return peak * envelope;
  };
}
